import { isOverdue } from './types';
import type { DealTask } from './dealTasks';

/**
 * Which deal tasks the Tasks tab is showing.
 *
 * ── Why "open" is the default and not "all" ────────────────────────────────
 *
 * Every task showed inline forever, completed ones mixed in with the rest. On
 * a deal worked for a month that is most of what somebody scrolls past to find
 * the thing that still needs doing.
 *
 * Kyu: "I want open tasks to be the default view so the tab stays focused on
 * what still needs action. Completed tasks should still be accessible, just
 * not mixed inline with open ones by default."
 *
 * So completed work is one chip away rather than gone — the same trade the
 * Sales Tracker Tasks tab makes, and the same vocabulary, so somebody moving
 * between the two tabs is not learning a second set of words for one idea.
 */

export type DealTaskFilter = 'open' | 'overdue' | 'today' | 'completed' | 'all';

export const DEAL_TASK_FILTER_LABELS: Record<DealTaskFilter, string> = {
  open: 'Open',
  overdue: 'Overdue',
  today: 'Due today',
  completed: 'Completed',
  all: 'All',
};

/** The order the chips read in: what needs doing, then what is done. */
export const DEAL_TASK_FILTERS: DealTaskFilter[] = ['open', 'overdue', 'today', 'completed', 'all'];

/**
 * `today` is an ISO date (YYYY-MM-DD).
 *
 * Overdue and Due today are subsets of open, deliberately: a completed task
 * that was due last week is not overdue, it is done. Counting it would make
 * the Overdue chip a number nobody can act on, which is the same failure as a
 * dashboard that counts unclassified leads as outbound.
 */
export function filterDealTasks(
  tasks: DealTask[],
  filter: DealTaskFilter,
  today: string,
): DealTask[] {
  if (filter === 'all') {
    return tasks;
  }
  if (filter === 'completed') {
    return tasks.filter((task) => task.is_completed);
  }
  const open = tasks.filter((task) => !task.is_completed);
  if (filter === 'open') {
    return open;
  }
  if (filter === 'overdue') {
    return open.filter((task) => task.due_date !== null && isOverdue(task.due_date, today));
  }
  return open.filter((task) => task.due_date === today);
}

/**
 * The order a flat list reads in.
 *
 * Kyu, Sep 8: "a normal task page where it's just a list of tasks, the
 * deadlines, the name of the task." With no deal headings to group under, the
 * date is the only structure the list has, so it has to be the right one:
 * what is late first, then what is due soonest, and the undated at the end
 * where they cannot hide a deadline. Ties keep their board position, and a
 * finished task never outranks an open one when a filter shows both.
 */
export function sortDealTasks(tasks: DealTask[], today: string): DealTask[] {
  const rank = (task: DealTask): number => {
    if (task.is_completed) return 3;
    if (task.due_date === null) return 2;
    if (isOverdue(task.due_date, today)) return 0;
    return 1;
  };
  return [...tasks].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    const aDue = a.due_date ?? '';
    const bDue = b.due_date ?? '';
    if (aDue !== bDue) return aDue < bDue ? -1 : 1;
    return a.position - b.position;
  });
}
